<script setup>
import { computed, nextTick, ref, watch } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: 'Confirmar ação' },
  description: { type: String, default: '' },
  confirmText: { type: String, default: 'Confirmar' },
  cancelText: { type: String, default: 'Cancelar' },
  confirmVariant: { type: String, default: 'danger' },
  isLoading: { type: Boolean, default: false },
  maxWidthClass: { type: String, default: 'max-w-[420px]' },
  icon: { type: String, default: '' },
  iconClass: { type: String, default: 'bg-n-blue-3 text-n-blue-11' },
});

const emit = defineEmits(['close', 'confirm']);

const dialogRef = ref(null);
const isDialogOpen = ref(false);
let suppressNextClose = false;

const dialogType = computed(() =>
  props.confirmVariant === 'primary' ? 'edit' : 'alert'
);

const dialogWidth = computed(() => {
  const width = props.maxWidthClass.match(
    /max-w-(5xl|4xl|3xl|2xl|xl|lg|md|sm)/
  )?.[1];
  return width || 'md';
});

const openDialog = async () => {
  await nextTick();
  if (!props.show || isDialogOpen.value || !dialogRef.value) return;

  dialogRef.value.open();
  isDialogOpen.value = true;
};

const closeDialog = () => {
  if (!isDialogOpen.value || !dialogRef.value) return;

  suppressNextClose = true;
  dialogRef.value.close();
};

const handleClose = () => {
  isDialogOpen.value = false;
  if (suppressNextClose) {
    suppressNextClose = false;
    return;
  }

  emit('close');
};

watch(
  () => props.show,
  isVisible => {
    if (isVisible) {
      openDialog();
      return;
    }

    closeDialog();
  },
  { immediate: true }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :type="dialogType"
    :title="title"
    :description="description"
    :cancel-button-label="cancelText"
    :confirm-button-label="confirmText"
    :is-loading="isLoading"
    :disable-confirm-button="isLoading"
    :width="dialogWidth"
    @close="handleClose"
    @confirm="emit('confirm')"
  >
    <template v-if="icon || $slots.icon" #description>
      <div class="flex items-start gap-3">
        <slot name="icon">
          <span
            v-if="icon"
            class="grid size-10 shrink-0 place-items-center rounded-full"
            :class="iconClass"
            aria-hidden="true"
          >
            <span class="size-5" :class="icon" />
          </span>
        </slot>
        <p v-if="description" class="mb-0 text-sm text-n-slate-11">
          {{ description }}
        </p>
      </div>
    </template>

    <slot />
  </Dialog>
</template>
